'use client';

import { useEffect, useState } from 'react';
import { WantekMark } from '@/components/header/WantekMark';
import { MOBILE_BREAKPOINT } from '@/components/shared/QualityProvider';

/**
 * Full-viewport splash that covers the journey while heavy chunks
 * (Three.js bundle + drei + fonts) are still in-flight. Renders from SSR
 * — pure DOM + CSS, no JS needed to appear — and the client effect below
 * only controls when to fade it out.
 *
 * Without this, slow connections show the page stitch together: stars
 * appear, then the W mark, then the loader card, then the 3D scene. With
 * this, the user sees one solid splash and a coordinated reveal.
 */
export function PageLoadingScreen() {
  // SSR + initial client paint both render `hidden=false` so the overlay
  // is visible from the first frame. The effect flips it true once the
  // journey chunk has resolved AND fonts have rendered, and after a small
  // minimum dwell so fast connections don't get a strobe.
  const [hidden, setHidden] = useState(false);
  const [removed, setRemoved] = useState(false);

  useEffect(() => {
    const MIN_VISIBLE_MS = 450;
    // Hard ceiling. Whatever happens — slow network, hung font promise,
    // chunk import that never settles — the splash MUST come down by
    // here so the user can interact with the page below. Tuned to fit
    // a slow 3G first-paint budget without feeling abandoned.
    const MAX_VISIBLE_MS = 4500;
    const startedAt = performance.now();

    // Mobile/tablet renders the static ProjectFeed, NOT the 3D journey
    // (QualityProvider forces 'low' tier below MOBILE_BREAKPOINT). Don't
    // make those users wait for a chunk they'll never render.
    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

    const journeyChunkReady = isMobile
      ? Promise.resolve()
      : import('@/components/journey/Journey').catch(() => null);

    // document.fonts.ready has been seen hanging indefinitely on iOS
    // Safari and Brave with custom fonts. Race it so a stalled font
    // load can't block the splash forever — we'd rather get a brief
    // FOUT than an infinite splash.
    const fontsBase =
      typeof document !== 'undefined' && 'fonts' in document
        ? document.fonts.ready
        : Promise.resolve();

    const timeoutAfter = (ms: number) =>
      new Promise<void>((resolve) => window.setTimeout(resolve, ms));

    let removeTimer: number | undefined;

    Promise.race([
      Promise.all([journeyChunkReady, fontsBase]),
      timeoutAfter(MAX_VISIBLE_MS),
    ]).then(() => {
      const elapsed = performance.now() - startedAt;
      const wait = Math.max(0, MIN_VISIBLE_MS - elapsed);
      window.setTimeout(() => {
        setHidden(true);
        // Remove from the DOM after the fade so the overlay never blocks
        // pointer events or eats z-index even with opacity:0.
        removeTimer = window.setTimeout(() => setRemoved(true), 700);
      }, wait);
    });

    return () => {
      if (removeTimer) window.clearTimeout(removeTimer);
    };
  }, []);

  if (removed) return null;

  return (
    <div
      className={`page-loading-screen${hidden ? ' is-hidden' : ''}`}
      aria-hidden={hidden}
      role="status"
    >
      <div className="page-loading-screen__inner">
        <div className="page-loading-screen__stage" aria-hidden="true">
          <WantekMark className="page-loading-screen__mark" />
          <span className="page-loading-screen__orbit-dot page-loading-screen__orbit-dot--1" />
          <span className="page-loading-screen__orbit-dot page-loading-screen__orbit-dot--2" />
          <span className="page-loading-screen__orbit-dot page-loading-screen__orbit-dot--3" />
        </div>
      </div>
    </div>
  );
}
